import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Download, DatabaseBackup, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const MISBackups = ({ canRun }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mis-backups"], queryFn: async () => (await api.get("/sales-mis/backups")).data });
  const [running, setRunning] = useState(false);
  const [dl, setDl] = useState("");

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/sales-mis/backups/run");
      toast.success("Backup snapshot stored safely off-server.");
      qc.invalidateQueries({ queryKey: ["mis-backups"] });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const download = async (b) => {
    setDl(b.id);
    try {
      const res = await api.get(`/sales-mis/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sales_MIS_Backup_${b.date_key}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDl("");
    }
  };

  const backups = data || [];
  return (
    <div data-testid="mis-backups-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
          A full Excel snapshot (board + every sheet + change log) is stored off-server automatically every night at 10pm IST — it survives even a total app crash.
        </p>
        {canRun && (
          <Button onClick={runNow} disabled={running} size="sm" className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="mis-backup-run-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}
            {running ? "Backing up…" : "Backup now"}
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Snapshot", "Type", "Created by", "Size", ""].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 text-slate-600" data-testid={`mis-backup-row-${b.id}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{fmtWhen(b.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={b.kind === "auto" ? "text-blue-700 border-blue-200 bg-blue-50" : "text-amber-700 border-amber-200 bg-amber-50"}>
                        {b.kind === "auto" ? "Nightly auto" : "Manual"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5">{b.created_by}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{fmtSize(b.size_bytes)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button variant="outline" size="sm" onClick={() => download(b)} disabled={dl === b.id} className="gap-1.5" data-testid={`mis-backup-download-${b.id}`}>
                        {dl === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download
                      </Button>
                    </td>
                  </tr>
                ))}
                {backups.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-400">No snapshots yet — the first auto backup runs tonight at 10pm IST, or click “Backup now”.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
